import {
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import installIcon from "@/assets/app-install-icon.svg";
import iphone from "@/assets/iphone.png";
import bell from "@/assets/bell.png";
import lock from "@/assets/lock.png";
import { Button } from "./ui/button";
import { ArrowLeftIcon, ArrowRightIcon } from "lucide-react";
import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import InstallAppInstructions from "@/components/InstallAppInstructions";
// pre-load images for next screen
import safariShare from "../assets/safari-share-button.png";
import safariAddToHome from "../assets/safari-add-to-home.png";
import safariAddToHome2 from "../assets/safari-add-to-home2.png";
import safari26MoreButton from "../assets/safari-26-more-button.png";
import safari26ShareButton from "../assets/safari-26-share-button.png";
import safari26SAddToHomeButton from "../assets/safari-26-add-to-home.png";
import safari26Confirm from "../assets/safari-26-confirm.png";
import androidMore from "../assets/android-more-button.png";
import androidAddToHome from "../assets/android-add-to-home.png";
import androidConfirm from "../assets/android-confirm.png";
import { TranslationTree } from "@/translations";

enum AppDialogStep {
  INFO = 1,
  INSTRUCTIONS = 2,
}

export default function AppInstallDialogContent({ t }: { t: TranslationTree }) {
  const [step, setStep] = useState<AppDialogStep>(AppDialogStep.INFO);
  const [previousStep, setPreviousStep] = useState<AppDialogStep | null>(null);
  const stepInitialX = previousStep === AppDialogStep.INSTRUCTIONS ? -50 : 50;

  useEffect(() => {
    if (step === AppDialogStep.INFO) {
      const preloadImages = [
        safariShare,
        safariAddToHome,
        safariAddToHome2,
        safari26MoreButton,
        safari26ShareButton,
        safari26SAddToHomeButton,
        safari26Confirm,
        androidMore,
        androidAddToHome,
        androidConfirm,
      ];
      preloadImages.forEach((src) => {
        const img = new Image();
        img.src = src;
      });
    }
  }, [step]);

  function InfoStep() {
    return (
      <>
        <DialogHeader>
          <DialogTitle className="flex flex-row items-center gap-4">
            <img src={installIcon} className="w-16" />
            <span className="text-xl mb-4">
              {t.APP_INSTALL_DIALOG.LABELS.DIALOG_TITLE}
            </span>
          </DialogTitle>
          <DialogDescription />
        </DialogHeader>
        <div className="grid grid-cols-[40px_1fr] gap-y-5 gap-x-3 w-fit items-center mx-auto">
          <img src={iphone} className="w-8 justify-self-center" />
          <span className="max-w-32">
            {t.APP_INSTALL_DIALOG.LABELS.APP_EXPERIENCE}
          </span>

          <img src={bell} className="w-9 justify-self-center" />
          <span>{t.APP_INSTALL_DIALOG.LABELS.NOTIFICATIONS}</span>

          <img src={lock} className="w-8 justify-self-center" />
          <span className="max-w-32">
            {t.APP_INSTALL_DIALOG.LABELS.SAVED_LOGIN}
          </span>
        </div>
        <div className="mt-5 flex flex-row w-full justify-end">
          <Button
            variant="default"
            className="items-center rounded-lg focus:outline-none focus:ring-0"
            onClick={() => {
              setStep(AppDialogStep.INSTRUCTIONS);
              setPreviousStep(AppDialogStep.INFO);
            }}
          >
            {t.APP_INSTALL_DIALOG.LABELS.INSTRUCTIONS_BUTTON}
            <ArrowRightIcon className="h-5 w-5 ml-2" />
          </Button>
        </div>
      </>
    );
  }

  function InfoStep2() {
    return (
      <>
        <DialogHeader>
          <DialogTitle className="flex flex-row">
            <Button
              variant="ghost"
              className="items-center rounded-lg w-20 px-0"
              onClick={() => {
                setStep(AppDialogStep.INFO);
                setPreviousStep(AppDialogStep.INSTRUCTIONS);
              }}
            >
              <ArrowLeftIcon className="h-4 w-4 mr-2" />
              {t.SHARED.LABELS.BACK_BUTTON}
            </Button>
          </DialogTitle>
          <DialogDescription />
        </DialogHeader>
        <InstallAppInstructions t={t} />
      </>
    );
  }

  return (
    <DialogContent className="dark text-white focus:outline-none max-w-sm max-h-[80vh] overflow-auto">
      <AnimatePresence mode="wait">
        {step === AppDialogStep.INFO ? (
          <motion.div
            key="email"
            initial={{ opacity: 0, x: stepInitialX }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -50 }}
            transition={{ duration: 0.1 }}
          >
            <InfoStep />
          </motion.div>
        ) : (
          <motion.div
            key="otp"
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -50 }}
            transition={{ duration: 0.1 }}
          >
            <InfoStep2 />
          </motion.div>
        )}
      </AnimatePresence>
    </DialogContent>
  );
}
